import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RARITY_STYLES, GRADES, GRADE_HELP, colorHex, COUNTRIES, REGION_OPTIONS, REGION_LABEL } from "@/lib/constants";
import { saveRecentlyViewed, getCachedInsulator } from "@/lib/recentlyViewed";
import { usePageMeta } from "@/lib/usePageMeta";
import { ComboInput } from "@/components/ComboInput";
import { useCatalogOptions } from "@/lib/useCatalogOptions";
import { ArrowLeft, Plus, Gauge, DollarSign, Heart, ChevronLeft, ChevronRight, Pencil, WifiOff, X, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ZoomImage } from "@/components/ZoomImage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

export default function InsulatorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const opts = useCatalogOptions();
  const navigate = useNavigate();
  const [ins, setIns] = useState(null);
  const [offline, setOffline] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [editUploading, setEditUploading] = useState(false);
  usePageMeta(
    ins ? `${ins.cd_number ? ins.cd_number + " " : ""}${ins.name}${ins.maker ? " — " + ins.maker : ""}${ins.color ? " " + ins.color : ""} Insulator`.trim() : "",
    ins ? (ins.description || `${ins.name}${ins.cd_number ? " (" + ins.cd_number + ")" : ""}${ins.maker ? " by " + ins.maker : ""}${ins.color ? ", " + ins.color : ""}. Details, grading, value and photos on Drops of Honey.`) : ""
  );
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ grade: "A", quantity: 1, variant_color: "", features: "", embossing: "", purchase_price: 0, storage_location: "", condition_notes: "", us_state: "", photos: [] });
  const [myPhotoUploading, setMyPhotoUploading] = useState(false);
  const uploadMyPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setMyPhotoUploading(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        setForm((f) => ({ ...f, photos: [...(f.photos || []), url] }));
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setMyPhotoUploading(false);
    }
  };
  const [contribOpen, setContribOpen] = useState(false);
  const [contrib, setContrib] = useState({ description: "", year: "", note: "", maker: "", color: "" });
  const [contribImages, setContribImages] = useState([]);
  const [contribCaptions, setContribCaptions] = useState({});
  const [cUploading, setCUploading] = useState(false);

  const uploadContribImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCUploading(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        setContribImages((p) => [...p, url]);
      }
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setCUploading(false);
    }
  };

  const submitContribution = async () => {
    const fields = {};
    if (contrib.description) fields.description = contrib.description;
    if (contrib.year) fields.year = contrib.year;
    if (contrib.maker) fields.maker = contrib.maker;
    if (contrib.color) fields.color = contrib.color;
    if (!contribImages.length && !Object.keys(fields).length && !contrib.note) {
      return toast.error("Add a photo or some information");
    }
    try {
      await api.post(`/insulators/${id}/contribute`, { images: contribImages, image_captions: contribCaptions, fields, note: contrib.note });
      toast.success("Thanks! Your addition was submitted for admin approval.");
      setContribOpen(false);
      setContrib({ description: "", year: "", note: "", maker: "", color: "" });
      setContribImages([]);
      setContribCaptions({});
    } catch (e) {
      toast.error("Could not submit");
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get(`/insulators/${id}`)
      .then(({ data }) => {
        if (cancelled) return;
        setIns(data);
        setOffline(false);
        saveRecentlyViewed(data);
      })
      .catch(() => {
        if (cancelled) return;
        const cached = getCachedInsulator(id);
        if (cached) { setIns(cached); setOffline(true); }
        else setIns(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  const addToCollection = async () => {
    try {
      const { data } = await api.post("/collection", { insulator_id: id, ...form });
      toast.success(data.merged ? `Count updated — you now have ${data.quantity} × ${ins.name}` : `${ins.name} added to your collection`);
      setOpen(false);
    } catch (e) {
      toast.error("Could not add to collection");
    }
  };

  const deleteInsulator = async () => {
    const label = isAdmin ? "from the catalog" : "— this was an entry you added";
    if (!window.confirm(`Delete "${ins.name}" ${label}? This cannot be undone.`)) return;
    try {
      await api.delete(`/insulators/${id}`);
      toast.success("Insulator deleted");
      navigate("/catalog");
    } catch (e) {
      const msg = e.response?.status === 409
        ? "Other collectors already have this piece — please ask an admin to remove it."
        : e.response?.data?.detail || "Could not delete insulator";
      toast.error(msg);
    }
  };

  const addToWishlist = async () => {
    if (!user) return navigate("/login");
    try {
      const { data } = await api.post("/wishlist", { insulator_id: id });
      toast.success(data.message === "Already on wishlist" ? "Already on your wishlist" : "Added to your wishlist");
    } catch (e) {
      toast.error("Could not add to wishlist");
    }
  };

  const isAdmin = user && (user.role === "admin" || user.role === "owner");
  const canEditEntry = isAdmin || (user && ins && ins.created_by === user.id);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const openEdit = () => {
    setEditForm({
      cd_number: ins.cd_number || "", name: ins.name || "", maker: ins.maker || "", color: ins.color || "",
      rarity: ins.rarity || "Common", rarity_index: ins.rarity_index || 3, year: ins.year || "",
      thread_type: ins.thread_type || "", base_type: ins.base_type || "", description: ins.description || "",
      country: ins.country || "", material: ins.material || "Glass", embossing: ins.embossing || "",
      est_value_low: ins.est_value_low || 0, est_value_high: ins.est_value_high || 0,
      images: ins.images?.length ? [...ins.images] : (ins.image_url ? [ins.image_url] : []),
      image_url: ins.image_url || "",
    });
    setEditOpen(true);
  };
  const uploadEditImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setEditUploading(true);
    try {
      for (const file of files) {
        const url = await uploadFile(file);
        setEditForm((f) => {
          const images = [...(f.images || []), url];
          return { ...f, images, image_url: f.image_url || images[0] };
        });
      }
      toast.success("Photo(s) added");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setEditUploading(false);
    }
  };
  const removeEditImage = (url) => {
    setEditForm((f) => {
      const images = (f.images || []).filter((x) => x !== url);
      return { ...f, images, image_url: f.image_url === url ? (images[0] || "") : f.image_url };
    });
  };
  const makeCover = (url) => setEditForm((f) => ({ ...f, image_url: url }));
  const saveEdit = async () => {
    if (!(editForm.name || "").trim()) { toast.error("Name cannot be empty."); return; }
    try {
      const payload = { ...editForm, rarity_index: parseInt(editForm.rarity_index) || 3, est_value_low: parseFloat(editForm.est_value_low) || 0, est_value_high: parseFloat(editForm.est_value_high) || 0 };
      const endpoint = isAdmin ? `/admin/insulators/${id}` : `/insulators/${id}`;
      const { data } = await api.put(endpoint, payload);
      setIns(data);
      setEditOpen(false);
      toast.success("Insulator updated");
    } catch (e) {
      toast.error("Could not save changes");
    }
  };

  if (ins === false) return <div className="max-w-7xl mx-auto px-4 py-24 text-center font-display text-2xl">Insulator not found.</div>;
  if (!ins) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-page animate-pulse font-tech">Loading…</div>;

  const images = ins.images?.length ? ins.images : [ins.image_url];
  const captions = ins.image_captions || {};
  const activeUrl = images[activeImg];
  const activeCaption = activeUrl ? captions[activeUrl] : "";
  const swatch = colorHex(ins.color);
  const canonCountry = ["United States", "USA", "US"].includes(ins.country) ? "United States of America" : ins.country;
  const regionOptions = REGION_OPTIONS[canonCountry];
  const regionLabel = REGION_LABEL[canonCountry] || "Region";
  const specs = [
    ["CD Number", ins.cd_number], ["Manufacturer", ins.maker], ["Material", ins.material],
    ["Color", ins.color], ["Style / Name", ins.name], ["Thread Type", ins.thread_type],
    ["Base Type", ins.base_type], ["Embossing", ins.embossing], ["Country", ins.country], ["Era", ins.year], ["Patent Date", ins.patent_date],
  ].filter(([, v]) => v);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
      <Link to="/catalog" className="inline-flex items-center gap-1 text-sm text-muted-page hover:brand-text mb-6"><ArrowLeft className="w-4 h-4" /> Back to catalog</Link>

      {offline && (
        <div data-testid="offline-banner" className="mb-5 flex items-center gap-2 text-sm rounded-xl px-4 py-2.5 surface border-2" style={{ borderColor: "var(--honey-500)" }}>
          <WifiOff className="w-4 h-4 brand-text shrink-0" /> You're offline — showing your saved copy of this piece.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <div className="surface border rounded-2xl overflow-hidden aspect-square glass-glow flex items-center justify-center relative p-6" style={{ background: `radial-gradient(circle at 50% 35%, ${swatch}22, transparent 70%)` }}>
            {activeUrl ? (
              <ZoomImage src={activeUrl} alt={ins.name} onEnlarge={() => setLightbox(true)} />
            ) : <span className="text-muted-page">No image</span>}
            {images.length > 1 && (
              <>
                <button data-testid="gallery-prev" onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full surface border grid place-items-center hover:shadow-lg z-10"><ChevronLeft className="w-5 h-5" /></button>
                <button data-testid="gallery-next" onClick={() => setActiveImg((i) => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full surface border grid place-items-center hover:shadow-lg z-10"><ChevronRight className="w-5 h-5" /></button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 font-tech text-xs px-2 py-1 rounded-full surface border z-10">{activeImg + 1} / {images.length}</span>
              </>
            )}
          </div>
          {activeCaption && (
            <div data-testid="active-image-caption" className="mt-3 flex items-start gap-2 surface border rounded-xl px-4 py-3">
              <MessageSquare className="w-4 h-4 brand-text shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{activeCaption}</p>
            </div>
          )}
          <p className="mt-2 text-xs text-muted-page font-tech text-center hidden md:block">Hover to zoom · click to enlarge</p>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {images.map((im, i) => (
                <button key={im} onClick={() => setActiveImg(i)} className={`relative w-16 h-16 rounded-lg overflow-hidden border-2 ${i === activeImg ? "border-amber-500" : "border-transparent"}`} data-testid={`gallery-thumb-${i}`}>
                  <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover" />
                  {captions[im] && <span className="absolute bottom-0 right-0 w-4 h-4 grid place-items-center rounded-tl-md" style={{ background: "var(--honey-500)" }}><MessageSquare className="w-2.5 h-2.5 text-white" /></span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <Dialog open={lightbox} onOpenChange={setLightbox}>
          <DialogContent className="surface max-w-5xl w-[95vw] p-0 overflow-hidden" data-testid="image-lightbox">
            <DialogTitle className="sr-only">{ins.name} image viewer</DialogTitle>
            <div className="relative bg-black/90 flex items-center justify-center" style={{ minHeight: "60vh", maxHeight: "82vh" }}>
              {activeUrl && <img src={mediaUrl(activeUrl)} alt={ins.name} className="max-w-full max-h-[82vh] object-contain" />}
              <button data-testid="lightbox-close" onClick={() => setLightbox(false)} className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 grid place-items-center hover:bg-white"><X className="w-5 h-5" /></button>
              {images.length > 1 && (
                <>
                  <button data-testid="lightbox-prev" onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 grid place-items-center hover:bg-white"><ChevronLeft className="w-6 h-6" /></button>
                  <button data-testid="lightbox-next" onClick={() => setActiveImg((i) => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 grid place-items-center hover:bg-white"><ChevronRight className="w-6 h-6" /></button>
                  <span className="absolute bottom-3 left-1/2 -translate-x-1/2 font-tech text-xs px-3 py-1.5 rounded-full bg-white/90">{activeImg + 1} / {images.length}</span>
                </>
              )}
            </div>
            {activeCaption && (
              <div className="px-6 py-4 flex items-start gap-2">
                <MessageSquare className="w-4 h-4 brand-text shrink-0 mt-0.5" />
                <p className="text-sm leading-relaxed">{activeCaption}</p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-tech px-2 py-1 rounded" style={{ background: "var(--honey-100)", color: "var(--honey-900)" }}>{ins.cd_number}</span>
            <span className={`text-xs font-tech uppercase tracking-wider px-2 py-1 rounded-full border ${RARITY_STYLES[ins.rarity] || RARITY_STYLES.Common}`}>{ins.rarity}</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">{ins.name}</h1>
          <p className="text-muted-page mt-1">{ins.maker} · {ins.color}</p>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="surface border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><Gauge className="w-4 h-4" /> Rarity Index</div>
              <p className="font-tech text-3xl font-bold mt-1 brand-text">{ins.rarity_index}<span className="text-lg text-muted-page">/10</span></p>
            </div>
            <div className="surface border rounded-xl p-4">
              <div className="flex items-center gap-2 text-muted-page text-xs font-tech uppercase tracking-widest"><DollarSign className="w-4 h-4" /> Value Guide</div>
              <p className="font-tech text-2xl font-bold mt-1 brand-text">{ins.est_value_low || ins.est_value_high ? `$${ins.est_value_low}–$${ins.est_value_high}` : <span className="text-muted-page">Not priced</span>}</p>
            </div>
          </div>

          <p className="mt-6 leading-relaxed">{ins.description}</p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {user ? (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <button data-testid="add-to-collection-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={{ background: "var(--brand)", color: "#fff" }}>
                    <Plus className="w-4 h-4" /> Add to my collection
                  </button>
                </DialogTrigger>
                <DialogContent className="surface">
                  <DialogHeader><DialogTitle className="font-display text-2xl">Add {ins.name}</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Grade</label>
                      <select data-testid="grade-select-dropdown" value={form.grade} onChange={(e) => setForm((f) => ({ ...f, grade: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2">
                        {GRADES.map((g) => <option key={g} value={g}>{g} — {GRADE_HELP[g]}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Quantity owned</label>
                        <select data-testid="quantity-owned-input" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: parseInt(e.target.value) || 1 }))} className="w-full surface border rounded-lg px-3 py-2">
                          {Array.from({ length: 25 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Purchase price ($)</label>
                        <input type="number" min={0} step="0.01" value={form.purchase_price} onFocus={(e) => { if (Number(form.purchase_price) === 0) setForm((f) => ({ ...f, purchase_price: "" })); }} onBlur={(e) => { if (e.target.value === "") setForm((f) => ({ ...f, purchase_price: 0 })); }} onChange={(e) => setForm((f) => ({ ...f, purchase_price: e.target.value === "" ? "" : parseFloat(e.target.value) || 0 }))} className="w-full surface border rounded-lg px-3 py-2" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Color / variant</label>
                        <input data-testid="variant-color-input" value={form.variant_color} onChange={(e) => setForm((f) => ({ ...f, variant_color: e.target.value }))} placeholder={ins.color} className="w-full surface border rounded-lg px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Features / marking</label>
                        <input data-testid="variant-features-input" value={form.features} onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))} placeholder="e.g. sharp drips, swirls" className="w-full surface border rounded-lg px-3 py-2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Embossing</label>
                      <input data-testid="variant-embossing-input" value={form.embossing} onChange={(e) => setForm((f) => ({ ...f, embossing: e.target.value }))} placeholder={ins.embossing || "e.g. HEMINGRAY-42 / MADE IN U.S.A."} className="w-full surface border rounded-lg px-3 py-2" />
                      <p className="text-[11px] text-muted-page mt-1">The exact raised lettering/markings on your piece.</p>
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Storage location</label>
                      <input value={form.storage_location} onChange={(e) => setForm((f) => ({ ...f, storage_location: e.target.value }))} placeholder="Shelf A, box 3…" className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">{regionLabel} <span className="normal-case tracking-normal">(lights up your regions map)</span></label>
                      {regionOptions ? (
                        <ComboInput testId="us-state-select" value={form.us_state} onChange={(v) => setForm((f) => ({ ...f, us_state: v }))} options={regionOptions} placeholder={`Select a ${regionLabel.toLowerCase()}…`} />
                      ) : (
                        <p className="text-sm text-muted-page px-3 py-2 surface border rounded-lg">No regions available for {ins.country || "this country"}.</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Your own photo(s) <span className="normal-case tracking-normal">— shown on your collection, not the shared catalog</span></label>
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 rounded-full surface border text-sm font-semibold">
                        {myPhotoUploading ? <span className="animate-pulse">Uploading…</span> : <><Plus className="w-4 h-4" /> Add my photo(s)</>}
                        <input type="file" accept="image/*" multiple className="hidden" onChange={uploadMyPhotos} data-testid="my-collection-photo-input" />
                      </label>
                      {form.photos?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {form.photos.map((p) => (
                            <div key={p} className="relative">
                              <img src={mediaUrl(p)} alt="" className="w-16 h-16 object-cover rounded-lg border" style={{ borderColor: "var(--page-border)" }} />
                              <button type="button" data-testid="my-photo-remove" onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((x) => x !== p) }))} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-xs">×</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Notes</label>
                      <textarea value={form.condition_notes} onChange={(e) => setForm((f) => ({ ...f, condition_notes: e.target.value }))} rows={2} className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                  </div>
                  <DialogFooter>
                    <button data-testid="confirm-add-collection-btn" onClick={addToCollection} className="px-5 py-2.5 rounded-full font-semibold w-full" style={{ background: "var(--brand)", color: "#fff" }}>Add to collection</button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            ) : (
              <button onClick={() => navigate("/login")} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold surface border">Sign in to add to collection</button>
            )}
            <button data-testid="add-to-wishlist-btn" onClick={addToWishlist} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold surface border hover:shadow">
              <Heart className="w-4 h-4" /> Wishlist
            </button>
            {canEditEntry && (
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild>
                  <button data-testid="admin-edit-insulator-btn" onClick={openEdit} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white" style={{ background: "var(--brand-strong)" }}>
                    <Pencil className="w-4 h-4" /> {isAdmin ? "Edit (admin)" : "Edit my entry"}
                  </button>
                </DialogTrigger>
                <DialogContent className="surface max-h-[85vh] overflow-y-auto">
                  <DialogHeader><DialogTitle className="font-display text-2xl">Edit insulator</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Photos</label>
                      <div className="flex gap-2 flex-wrap mb-2">
                        {(editForm.images || []).map((im) => (
                          <div key={im} className="relative w-20 h-20 rounded-lg overflow-hidden border" style={{ borderColor: "var(--page-border)" }} data-testid="edit-photo-thumb">
                            <img src={mediaUrl(im)} alt="" className="w-full h-full object-cover" />
                            <button type="button" onClick={() => removeEditImage(im)} data-testid="edit-photo-remove" className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-xs"><Trash2 className="w-3 h-3" /></button>
                            {editForm.image_url === im
                              ? <span className="absolute bottom-0 inset-x-0 text-[9px] text-center text-white bg-black/60">Cover</span>
                              : <button type="button" onClick={() => makeCover(im)} className="absolute bottom-0 inset-x-0 text-[9px] text-center text-white bg-black/40 hover:bg-black/70">Set cover</button>}
                          </div>
                        ))}
                        <label className="cursor-pointer w-20 h-20 rounded-lg border-2 border-dashed grid place-items-center" style={{ borderColor: "var(--page-border)" }} data-testid="edit-photo-add">
                          {editUploading ? <span className="text-[10px] animate-pulse">…</span> : <Plus className="w-5 h-5 text-muted-page" />}
                          <input type="file" accept="image/*" multiple className="hidden" onChange={uploadEditImages} data-testid="edit-photo-input" />
                        </label>
                      </div>
                      <p className="text-[11px] text-muted-page">Upload real photos to replace the stock image. First photo (or "Set cover") is the main image.</p>
                    </div>
                    <ComboInput label="Name" testId="edit-name-input" value={editForm.name} onChange={(v) => setEditForm((f) => ({ ...f, name: v }))} options={opts.names} />
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">CD / U Number</label>
                      <input data-testid="edit-cd_number-input" value={editForm.cd_number} onChange={(e) => setEditForm((f) => ({ ...f, cd_number: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                    <ComboInput label="Maker" testId="edit-maker-input" value={editForm.maker} onChange={(v) => setEditForm((f) => ({ ...f, maker: v }))} options={opts.makers} />
                    <ComboInput label="Color" testId="edit-color-input" value={editForm.color} onChange={(v) => setEditForm((f) => ({ ...f, color: v }))} options={opts.colors} />
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Era / Year</label>
                      <input data-testid="edit-year-input" value={editForm.year} onChange={(e) => setEditForm((f) => ({ ...f, year: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                    <ComboInput label="Thread type" testId="edit-thread_type-input" value={editForm.thread_type} onChange={(v) => setEditForm((f) => ({ ...f, thread_type: v }))} options={opts.thread_types} />
                    <ComboInput label="Base type" testId="edit-base_type-input" value={editForm.base_type} onChange={(v) => setEditForm((f) => ({ ...f, base_type: v }))} options={opts.base_types} />
                    <ComboInput label="Country of origin" testId="edit-country-input" value={editForm.country || ""} onChange={(v) => setEditForm((f) => ({ ...f, country: v }))} options={opts.countries.length ? opts.countries : COUNTRIES} />
                    <ComboInput label="Material" testId="edit-material-input" value={editForm.material || "Glass"} onChange={(v) => setEditForm((f) => ({ ...f, material: v }))} options={opts.materials} />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Rarity</label>
                        <select value={editForm.rarity} onChange={(e) => setEditForm((f) => ({ ...f, rarity: e.target.value }))} className="w-full surface border rounded-lg px-2 py-2">
                          {["Common", "Scarce", "Rare", "Very Rare", "Ultra Rare / Museum"].map((r) => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Index</label>
                        <input type="number" min={1} max={10} value={editForm.rarity_index} onChange={(e) => setEditForm((f) => ({ ...f, rarity_index: e.target.value }))} className="w-full surface border rounded-lg px-2 py-2" />
                      </div>
                      <div>
                        <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Value Low ($)</label>
                        <input type="number" value={editForm.est_value_low} onFocus={(e) => { if (String(editForm.est_value_low) === "0") setEditForm((f) => ({ ...f, est_value_low: "" })); }} onBlur={(e) => { if (e.target.value === "") setEditForm((f) => ({ ...f, est_value_low: 0 })); }} onChange={(e) => setEditForm((f) => ({ ...f, est_value_low: e.target.value }))} className="w-full surface border rounded-lg px-2 py-2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Value High ($)</label>
                      <input type="number" value={editForm.est_value_high} onFocus={(e) => { if (String(editForm.est_value_high) === "0") setEditForm((f) => ({ ...f, est_value_high: "" })); }} onBlur={(e) => { if (e.target.value === "") setEditForm((f) => ({ ...f, est_value_high: 0 })); }} onChange={(e) => setEditForm((f) => ({ ...f, est_value_high: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Embossing</label>
                      <input data-testid="edit-embossing-input" value={editForm.embossing || ""} onChange={(e) => setEditForm((f) => ({ ...f, embossing: e.target.value }))} placeholder="e.g. HEMINGRAY-42 / MADE IN U.S.A." className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Description</label>
                      <textarea data-testid="edit-description-input" value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} rows={3} className="w-full surface border rounded-lg px-3 py-2" />
                    </div>
                  </div>
                  <DialogFooter><button data-testid="save-edit-insulator-btn" onClick={saveEdit} className="px-5 py-2.5 rounded-full font-semibold w-full text-white" style={{ background: "var(--brand)" }}>Save changes</button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {(isAdmin || (user && ins.created_by === user.id)) && (
              <button data-testid="admin-delete-insulator-btn" onClick={deleteInsulator} className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100">
                <Trash2 className="w-4 h-4" /> {isAdmin ? "Delete (admin)" : "Delete my entry"}
              </button>
            )}
            {user && (
              <Dialog open={contribOpen} onOpenChange={setContribOpen}>
                <DialogTrigger asChild>
                  <button data-testid="suggest-addition-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold surface border hover:shadow">
                    <Plus className="w-4 h-4" /> Suggest an addition
                  </button>
                </DialogTrigger>
                <DialogContent className="surface">
                  <DialogHeader><DialogTitle className="font-display text-2xl">Add photos of your insulator</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-page -mt-2">Add photos of your <em>actual</em> insulator — we recommend at least 3 (front, back, and any embossing or damage). Note any unique features. Your submission is reviewed by an admin before it appears in the catalog.</p>
                  <div className="space-y-4">
                    <div>
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
                        {cUploading ? <span className="animate-pulse">Uploading…</span> : <><Plus className="w-4 h-4" /> Add photo(s)</>}
                        <input type="file" accept="image/*" multiple className="hidden" onChange={uploadContribImages} data-testid="contrib-photo-input" />
                      </label>
                      <p className={`mt-2 text-xs font-tech ${contribImages.length >= 3 ? "brand-text" : "text-muted-page"}`} data-testid="contrib-photo-count">
                        {contribImages.length} photo{contribImages.length === 1 ? "" : "s"} added{contribImages.length < 3 ? ` · add ${3 - contribImages.length} more to reach the recommended 3` : " · looks great!"}
                      </p>
                      {contribImages.length > 0 && (
                        <div className="space-y-3 mt-3">
                          {contribImages.map((im, i) => (
                            <div key={im} className="flex gap-3 items-start" data-testid={`contrib-photo-row-${i}`}>
                              <div className="relative shrink-0">
                                <img src={mediaUrl(im)} alt="" className="w-20 h-20 object-cover rounded-lg border" style={{ borderColor: "var(--page-border)" }} />
                                <button type="button" onClick={() => { setContribImages((p) => p.filter((x) => x !== im)); setContribCaptions((c) => { const n = { ...c }; delete n[im]; return n; }); }} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-rose-600 text-white grid place-items-center text-xs" data-testid={`contrib-photo-remove-${i}`}>×</button>
                              </div>
                              <textarea
                                data-testid={`contrib-photo-caption-${i}`}
                                value={contribCaptions[im] || ""}
                                onChange={(e) => setContribCaptions((c) => ({ ...c, [im]: e.target.value }))}
                                rows={2}
                                placeholder="Comment on this photo — e.g. chip on skirt, amber swirl, base damage…"
                                className="flex-1 surface border rounded-lg px-3 py-2 text-sm"
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Additional / corrected description</label>
                      <textarea data-testid="contrib-description-input" value={contrib.description} onChange={(e) => setContrib((c) => ({ ...c, description: e.target.value }))} rows={3} className="w-full surface border rounded-lg px-3 py-2.5" />
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Era / Year</label>
                      <input data-testid="contrib-year-input" value={contrib.year} onChange={(e) => setContrib((c) => ({ ...c, year: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <ComboInput label="Maker" testId="contrib-maker-input" value={contrib.maker} onChange={(v) => setContrib((c) => ({ ...c, maker: v }))} options={opts.makers} placeholder="Select or add…" />
                      <ComboInput label="Color" testId="contrib-color-input" value={contrib.color} onChange={(v) => setContrib((c) => ({ ...c, color: v }))} options={opts.colors} placeholder="Select or add…" />
                    </div>
                    <div>
                      <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Note to admin (optional)</label>
                      <input data-testid="contrib-note-input" value={contrib.note} onChange={(e) => setContrib((c) => ({ ...c, note: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
                    </div>
                  </div>
                  <DialogFooter><button data-testid="submit-contribution-btn" onClick={submitContribution} className="px-5 py-2.5 rounded-full font-semibold w-full text-white" style={{ background: "var(--brand)" }}>Submit for approval</button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>
      </div>

      {/* Specs table */}
      <div className="grid lg:grid-cols-2 gap-8 mt-14">
        <div>
          <h2 className="font-display text-2xl font-bold mb-4">Key Specifications</h2>
          <div className="surface border rounded-2xl overflow-hidden">
            {specs.map(([k, v], i) => (
              <div key={k} className={`flex justify-between px-5 py-3 ${i % 2 ? "" : "bg-black/[0.02]"}`}>
                <span className="text-muted-page">{k}</span>
                <span className="font-medium font-tech text-sm">{v}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold mb-4">Eagle Cap Collectibles Grading Scale</h2>
          <div className="surface border rounded-2xl overflow-hidden">
            {GRADES.map((g, i) => (
              <div key={g} className={`flex justify-between px-5 py-2.5 ${i % 2 ? "" : "bg-black/[0.02]"}`}>
                <span className="font-tech font-semibold">{g}</span>
                <span className="text-muted-page text-sm text-right">{GRADE_HELP[g]}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
